/**
 * Set-piece tactics — §6.5 of the tactics board spec.
 *
 * Coaches build dedicated set-piece plays: attacking and defending
 * corners, free kicks (long / short / wide), throw-ins, penalties.
 * Each play is a structured snapshot:
 *
 *   - type        — one of `SET_PIECE_TYPES`
 *   - origin      — world `{x, z}` of the ball (corner spot / FK spot /
 *                   throw-in location)
 *   - delivery    — `'in-swinger' | 'out-swinger' | 'short' | 'long' |
 *                   'low' | 'driven'` (some only apply to corners)
 *   - homeSpots / awaySpots — 11 + 11 slot snapshots `{role, x, z}`
 *   - arrows      — 0..N TacticsArrow runs after the ball is played
 *   - name + description — coach copy
 *
 * The base `TacticsSetPiece` slot on `TacticsLayout` is intentionally
 * permissive (`Record<string, unknown> & { id: string }` — see
 * `./types.ts`) so this module owns the concrete shape.
 *
 * Coordinate system mirrors the rest of the tactics package:
 * world metres, pitch-centred origin. The 2D renderer applies the
 * `SVG-x = -world.z, SVG-y = world.x` axis swap when drawing — see
 * `apps/web2/src/components/tactics2d/`.
 */

import type { TacticsArrow } from './types';
import { FIELD_LENGTH_M, FIELD_WIDTH_M } from './adapters';

// Re-export so callers don't have to import TacticsArrow from a sibling.
export type { TacticsArrow } from './types';

// ---------------------------------------------------------------------------
// String-union enums — exported as const tuples so callers can iterate
// at runtime (tab strip, validation, etc.).
// ---------------------------------------------------------------------------

export const SET_PIECE_TYPES = [
  'corner-att',
  'corner-def',
  'freekick-att',
  'freekick-def',
  'throwin-att',
  'throwin-def',
  'penalty-att',
  'penalty-def',
] as const;
export type SetPieceType = (typeof SET_PIECE_TYPES)[number];

export const DELIVERY_STYLES = [
  'in-swinger',
  'out-swinger',
  'short',
  'long',
  'low',
  'driven',
] as const;
export type DeliveryStyle = (typeof DELIVERY_STYLES)[number];

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** A single position slot for a set-piece snapshot.
 *  No `y` — set-piece layouts are pure top-down (the 3D renderer can
 *  layer its own figure-height when it applies the snapshot). */
export interface SetPieceSpot {
  role: string;
  x: number;
  z: number;
}

/** One named set-piece play, saved on a `TacticsLayout.setPieces[]`
 *  or in the standalone library. */
export interface TacticsSetPiece {
  id: string;
  name: string;
  description?: string;
  type: SetPieceType;
  /** World metres — where the ball is placed (corner / FK / throw-in /
   *  penalty spot). For corners this is `originForCorner(...)`; for
   *  penalties `originForCentreSpot()` returns the penalty mark. */
  origin: { x: number; z: number };
  /** Delivery style — undefined for penalties (binary outcome) and
   *  free for the others. Caller should validate against
   *  `validDeliveriesForType(type)`. */
  delivery?: DeliveryStyle;
  /** 11 attacker positions in this set-piece. */
  homeSpots: SetPieceSpot[];
  /** 11 defender positions in this set-piece. */
  awaySpots: SetPieceSpot[];
  /** Movement runs (re-uses the canonical TacticsArrow shape so the
   *  same 2D/3D renderers can draw them). */
  arrows: TacticsArrow[];
  /** ISO timestamp — minted at save by the store. */
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Origin helpers
// ---------------------------------------------------------------------------

/**
 * World position of a corner flag.
 *
 * Coordinate model: pitch-centred origin. +Z is the home end (own
 * goal), −Z is the away end (attacking goal). Width spans ±FIELD_WIDTH/2
 * along x; length spans ±FIELD_LENGTH/2 along z.
 *
 * @param side  - 'left' or 'right' touchline as seen from the home end.
 * @param end   - 'home' (positive z) or 'away' (negative z) end of pitch.
 */
export function originForCorner(
  side: 'left' | 'right',
  end: 'home' | 'away',
  pitchLengthM: number = FIELD_LENGTH_M,
  pitchWidthM: number = FIELD_WIDTH_M,
): { x: number; z: number } {
  const halfW = pitchWidthM / 2;
  const halfL = pitchLengthM / 2;
  return {
    x: side === 'left' ? -halfW : halfW,
    z: end === 'home' ? halfL : -halfL,
  };
}

/** Penalty mark — 11 metres in front of the away goal by default
 *  (attacking penalty). Pass `end='home'` for a defending-end penalty. */
export function originForCentreSpot(
  end: 'home' | 'away' = 'away',
  pitchLengthM: number = FIELD_LENGTH_M,
): { x: number; z: number } {
  const halfL = pitchLengthM / 2;
  // 11 m from the goal line, on the centre line.
  return {
    x: 0,
    z: end === 'home' ? halfL - 11 : -halfL + 11,
  };
}

/** Kickoff — pitch centre. */
export function originForKickoff(): { x: number; z: number } {
  return { x: 0, z: 0 };
}

// ---------------------------------------------------------------------------
// Field-slice regions
// ---------------------------------------------------------------------------

/**
 * A rectangular slice of the pitch in world metres (pitch-centred).
 * `x` is the width axis (±FIELD_WIDTH/2), `z` the length axis
 * (±FIELD_LENGTH/2). Consumed by the 2D renderer to crop the SVG
 * viewBox; the renderer owns the world→SVG axis swap.
 */
export interface FieldRegion {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** 18-yard ("penalty") box, cross-pitch width in metres. The set-piece
 *  camera for corners + penalties must keep the whole box in frame —
 *  that is where the players and the action are. */
const PENALTY_BOX_WIDTH_M = 40.32;
/** Breathing room kept around the box / ball when framing a slice. */
const SLICE_MARGIN_M = 6;

/** Window width for ball-centred set pieces (free kicks, throw-ins), as
 *  a fraction of the pitch width — the share of pitch the window spans
 *  around the ball. */
const BALL_CENTRED_ZOOM: Partial<Record<SetPieceType, number>> = {
  'freekick-att': 0.7,
  'freekick-def': 0.7,
  'throwin-att': 0.62,
  'throwin-def': 0.62,
};

/**
 * The slice of pitch worth showing for a given set piece — a FIFA-style
 * set-piece camera. Set pieces are local events, so the board frames a
 * window around the action instead of the whole 105 m pitch.
 *
 * Key properties:
 *  - the window keeps the **full pitch's aspect ratio** (length scales
 *    from width by it), so the slice reads as the same field just
 *    closer in and fills the board frame the way the full pitch does —
 *    never a letterboxed portrait strip;
 *  - for **corners + penalties** the window is wide enough to hold the
 *    whole 18-yard box and centred on x=0, so the box — where the
 *    players are — is always fully in frame;
 *  - for **free kicks / throw-ins** the window centres on the ball.
 *
 * The window is always clamped fully inside the pitch.
 */
export function fieldRegionForSetPiece(
  type: SetPieceType,
  origin: { x: number; z: number },
  pitchLengthM: number = FIELD_LENGTH_M,
  pitchWidthM: number = FIELD_WIDTH_M,
): FieldRegion {
  const halfL = pitchLengthM / 2;
  const halfW = pitchWidthM / 2;
  // The window keeps the full pitch's aspect ratio.
  const aspect = pitchLengthM / pitchWidthM;

  const goalEnd = type.startsWith('corner') || type.startsWith('penalty');

  // Window WIDTH (x-extent) sets the zoom; LENGTH (z) follows from the
  // locked aspect ratio.
  const winWid = goalEnd
    // Corners + penalties — wide enough to hold the whole 18-yard box.
    ? Math.min(pitchWidthM, PENALTY_BOX_WIDTH_M + SLICE_MARGIN_M * 2)
    // Free kicks / throw-ins — a fraction of the pitch around the ball.
    : pitchWidthM * (BALL_CENTRED_ZOOM[type] ?? 0.7);
  const winLen = winWid * aspect;

  // Focal point the window centres on (pre-clamp).
  // +1 = home end (+z), −1 = away end (−z).
  const endSign = origin.z >= 0 ? 1 : -1;
  const focalZ = goalEnd
    ? endSign * halfL - (endSign * winLen) / 2 // window edge on the goal line
    : origin.z;
  // Corners/penalties centre on x=0 so the box is symmetric + fully
  // framed; ball events centre on the ball.
  const focalX = goalEnd ? 0 : origin.x;

  // Clamp a window centre so the window stays fully on the pitch.
  const clampCentre = (c: number, half: number, win: number): number => {
    const h = win / 2;
    if (h >= half) return 0; // window wider than the pitch — centre it
    return Math.max(-half + h, Math.min(half - h, c));
  };
  const cz = clampCentre(focalZ, halfL, winLen);
  const cx = clampCentre(focalX, halfW, winWid);

  return {
    minZ: cz - winLen / 2,
    maxZ: cz + winLen / 2,
    minX: cx - winWid / 2,
    maxX: cx + winWid / 2,
  };
}

// ---------------------------------------------------------------------------
// Delivery rules
// ---------------------------------------------------------------------------

/**
 * Which delivery styles apply to a given set-piece type.
 *
 * - Corners + free kicks: swingers, plus short / long / low / driven.
 * - Throw-ins: short / long only (no curve on a thrown ball).
 * - Penalties: no delivery axis (binary "place / blast" is captured
 *   by the spot picking).
 */
export function validDeliveriesForType(
  type: SetPieceType,
): DeliveryStyle[] {
  switch (type) {
    case 'corner-att':
    case 'corner-def':
    case 'freekick-att':
    case 'freekick-def':
      return ['in-swinger', 'out-swinger', 'short', 'long', 'low', 'driven'];
    case 'throwin-att':
    case 'throwin-def':
      return ['short', 'long'];
    case 'penalty-att':
    case 'penalty-def':
      return [];
  }
}

// ---------------------------------------------------------------------------
// Default layouts
// ---------------------------------------------------------------------------

/**
 * Sensible starting layouts per set-piece type. Returns everything
 * except `id` and `createdAt` (the store mints those). The renderer
 * is free to nudge tokens once the coach drops them on the board.
 *
 * Layout strategy:
 * - Attacking corner: 4 attackers in the six-yard / penalty area
 *   plus a near-post runner and a short-corner option; 1 holding at
 *   the edge of the box for rebounds; rest stay back for transition.
 * - Defending corner: 6 defenders zonally in the six-yard / penalty
 *   area, 1 on each post, 2 marking near + far edge, the rest break.
 * - Attacking free kick: a wall-buster wide left, a runner attacking
 *   the back post, four in the box on a typical wide free kick.
 * - Defending free kick: 4-man wall + 4 zonal markers in the box.
 * - Attacking throw-in: short-option close to the thrower, near-post
 *   runner, third-man support; rest hold shape.
 * - Defending throw-in: ball-side overload, others compress laterally.
 * - Penalty (att/def): kicker placed at the spot, GK + 4 defenders on
 *   the D, rest spread outside the box for the rebound.
 */
export function defaultSetPiece(
  type: SetPieceType,
): Omit<TacticsSetPiece, 'id' | 'createdAt'> {
  const halfW = FIELD_WIDTH_M / 2;
  const halfL = FIELD_LENGTH_M / 2;

  // Map of "home spots somewhere safe" — used when a side is not the
  // protagonist (e.g. defenders during an attacking corner park near
  // halfway, attackers during a defending corner do the same).
  function deepHalf(team: 'home' | 'away'): SetPieceSpot[] {
    const sign = team === 'home' ? 1 : -1;
    const z0 = sign * (halfL * 0.4); // ~21 m from centre, own half-ish
    return [
      { role: 'GK', x: 0, z: sign * (halfL - 2) },
      { role: 'LB', x: -halfW * 0.4, z: z0 },
      { role: 'CB', x: -halfW * 0.15, z: z0 - sign * 2 },
      { role: 'CB', x: halfW * 0.15, z: z0 - sign * 2 },
      { role: 'RB', x: halfW * 0.4, z: z0 },
      { role: 'CM', x: -halfW * 0.25, z: z0 - sign * 8 },
      { role: 'CM', x: 0, z: z0 - sign * 10 },
      { role: 'CM', x: halfW * 0.25, z: z0 - sign * 8 },
      { role: 'LW', x: -halfW * 0.45, z: z0 - sign * 14 },
      { role: 'ST', x: 0, z: z0 - sign * 18 },
      { role: 'RW', x: halfW * 0.45, z: z0 - sign * 14 },
    ];
  }

  // Common "11 holding shape" — used for set-pieces that don't have a
  // strong default for one side.
  function holdingShape(team: 'home' | 'away'): SetPieceSpot[] {
    return deepHalf(team);
  }

  switch (type) {
    case 'corner-att': {
      // Attacking left corner into the away end (−Z).
      const origin = originForCorner('left', 'away');
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 2 },          // back on own goal
        { role: 'CB', x: -halfW * 0.25, z: halfL * 0.3 }, // sit deeper
        { role: 'CB', x: halfW * 0.25, z: halfL * 0.3 },
        { role: 'CK', x: origin.x + 1, z: origin.z + 1 }, // taker at flag
        { role: 'SR', x: -halfW * 0.35, z: -halfL * 0.85 }, // short option
        { role: 'NP', x: -3, z: -halfL + 5.5 },        // near-post runner
        { role: 'ST', x: 0, z: -halfL + 7 },           // centre, 6-yard
        { role: 'AM', x: 3, z: -halfL + 8 },           // far-post arrival
        { role: 'LW', x: 6, z: -halfL + 11 },          // far edge of 6-yard
        { role: 'CM', x: 0, z: -halfL + 18 },          // edge-of-box rebound
        { role: 'CM', x: -halfW * 0.15, z: 0 },        // transition cover
      ];
      return {
        name: 'Attacking corner (left)',
        type,
        origin,
        delivery: 'in-swinger',
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'corner-def': {
      // Defending an attacking corner at our own end (+Z).
      const origin = originForCorner('left', 'home');
      const awaySpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: -halfL + 2 },           // away GK far away
        { role: 'CK', x: origin.x + 1, z: origin.z - 1 },
        { role: 'NP', x: -3, z: halfL - 5.5 },
        { role: 'ST', x: 0, z: halfL - 7 },
        { role: 'AM', x: 3, z: halfL - 8 },
        { role: 'LW', x: 6, z: halfL - 11 },
        { role: 'SR', x: -halfW * 0.35, z: halfL * 0.85 },
        { role: 'CM', x: 0, z: halfL - 18 },
        { role: 'CB', x: -halfW * 0.25, z: -halfL * 0.3 },
        { role: 'CB', x: halfW * 0.25, z: -halfL * 0.3 },
        { role: 'CM', x: halfW * 0.15, z: 0 },
      ];
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 1 },               // on the line
        { role: 'NP', x: -3.5, z: halfL - 5.5 },          // near post
        { role: 'FP', x: 3.5, z: halfL - 5.5 },           // far post
        { role: 'CB', x: 0, z: halfL - 7 },               // 6-yard centre
        { role: 'CB', x: -1.5, z: halfL - 9 },            // zonal 1
        { role: 'CB', x: 1.5, z: halfL - 9 },             // zonal 2
        { role: 'CM', x: -3, z: halfL - 11 },             // zonal 3 (near-side)
        { role: 'CM', x: 3, z: halfL - 11 },              // zonal 4 (far-side)
        { role: 'MK', x: -halfW * 0.3, z: halfL - 13 },   // marker on short option
        { role: 'EB', x: 0, z: halfL - 17 },              // edge-of-box clearer
        { role: 'ST', x: 0, z: 0 },                       // breakaway forward
      ];
      return {
        name: 'Defending corner (left)',
        type,
        origin,
        delivery: 'in-swinger',
        homeSpots,
        awaySpots,
        arrows: [],
      };
    }

    case 'freekick-att': {
      // Wide attacking free kick on the left, ~25m from the away goal.
      const origin = { x: -halfW * 0.5, z: -halfL + 25 };
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 2 },
        { role: 'FK', x: origin.x, z: origin.z + 1 },    // taker
        { role: 'CB', x: -halfW * 0.25, z: halfL * 0.3 },
        { role: 'CB', x: halfW * 0.25, z: halfL * 0.3 },
        { role: 'NP', x: -3, z: -halfL + 5.5 },          // near-post runner
        { role: 'ST', x: 0, z: -halfL + 7 },
        { role: 'AM', x: 3, z: -halfL + 8 },
        { role: 'LW', x: 6, z: -halfL + 11 },
        { role: 'CM', x: 0, z: -halfL + 18 },             // edge of box
        { role: 'CM', x: -halfW * 0.2, z: -halfL * 0.4 }, // cover
        { role: 'RB', x: halfW * 0.4, z: 0 },             // wide cover
      ];
      return {
        name: 'Attacking free kick (wide left)',
        type,
        origin,
        delivery: 'out-swinger',
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'freekick-def': {
      // Defending a free kick on our left side, ~25m from our goal.
      const origin = { x: -halfW * 0.5, z: halfL - 25 };
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 1 },
        // 4-man wall ~9.15m from the ball.
        { role: 'W1', x: origin.x + 4, z: origin.z + 2.5 },
        { role: 'W2', x: origin.x + 5.5, z: origin.z + 2.5 },
        { role: 'W3', x: origin.x + 7, z: origin.z + 2.5 },
        { role: 'W4', x: origin.x + 8.5, z: origin.z + 2.5 },
        // Zonal markers across the box.
        { role: 'NP', x: -3, z: halfL - 5.5 },
        { role: 'ST', x: 0, z: halfL - 7 },
        { role: 'AM', x: 3, z: halfL - 8 },
        { role: 'LW', x: 6, z: halfL - 11 },
        { role: 'EB', x: 0, z: halfL - 17 },
        { role: 'CF', x: 0, z: 0 },                       // up-top outlet
      ];
      return {
        name: 'Defending free kick (wide left)',
        type,
        origin,
        delivery: 'in-swinger',
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'throwin-att': {
      // Attacking long throw on the left, ~halfway into the opposition half.
      const origin = { x: -halfW, z: -halfL * 0.3 };
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 2 },
        { role: 'TI', x: origin.x - 1, z: origin.z },     // thrower (just off pitch)
        { role: 'SH', x: origin.x + 4, z: origin.z + 1 }, // short option
        { role: 'TM', x: origin.x + 7, z: origin.z - 2 }, // third-man support
        { role: 'NP', x: -3, z: -halfL + 5.5 },
        { role: 'ST', x: 0, z: -halfL + 7 },
        { role: 'AM', x: 3, z: -halfL + 8 },
        { role: 'CM', x: 0, z: -halfL + 18 },
        { role: 'CB', x: -halfW * 0.25, z: halfL * 0.3 },
        { role: 'CB', x: halfW * 0.25, z: halfL * 0.3 },
        { role: 'RB', x: halfW * 0.4, z: 0 },
      ];
      return {
        name: 'Attacking throw-in (left)',
        type,
        origin,
        delivery: 'long',
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'throwin-def': {
      // Defending a throw-in on our left side, ~halfway into our half.
      const origin = { x: -halfW, z: halfL * 0.3 };
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 2 },
        // Ball-side overload — three players within 8m of the throw.
        { role: 'M1', x: origin.x + 4, z: origin.z - 1 },
        { role: 'M2', x: origin.x + 6, z: origin.z + 2 },
        { role: 'M3', x: origin.x + 8, z: origin.z - 3 },
        { role: 'CB', x: -halfW * 0.15, z: halfL * 0.2 },
        { role: 'CB', x: halfW * 0.15, z: halfL * 0.2 },
        { role: 'RB', x: halfW * 0.4, z: halfL * 0.25 },
        { role: 'CM', x: 0, z: 0 },
        { role: 'LB', x: -halfW * 0.4, z: halfL * 0.1 },   // covering full-back
        { role: 'LW', x: -halfW * 0.4, z: -halfL * 0.1 },
        { role: 'ST', x: 0, z: -halfL * 0.3 },             // up-top outlet
      ];
      return {
        name: 'Defending throw-in (left)',
        type,
        origin,
        delivery: 'short',
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'penalty-att': {
      const origin = originForCentreSpot('away');
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 2 },
        { role: 'PK', x: 0, z: origin.z + 1 },              // kicker
        // 5 attackers on the arc / edge of box for rebounds.
        { role: 'ST', x: -4, z: origin.z + 7 },
        { role: 'AM', x: 0, z: origin.z + 9 },
        { role: 'CM', x: 4, z: origin.z + 7 },
        { role: 'LW', x: -8, z: origin.z + 11 },
        { role: 'RW', x: 8, z: origin.z + 11 },
        // 4 behind the play for transition cover.
        { role: 'LB', x: -halfW * 0.4, z: 0 },
        { role: 'CB', x: -halfW * 0.2, z: halfL * 0.3 },
        { role: 'CB', x: halfW * 0.2, z: halfL * 0.3 },
        { role: 'RB', x: halfW * 0.4, z: 0 },
      ];
      return {
        name: 'Attacking penalty',
        type,
        origin,
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }

    case 'penalty-def': {
      const origin = originForCentreSpot('home');
      const homeSpots: SetPieceSpot[] = [
        { role: 'GK', x: 0, z: halfL - 1 },                 // on the line
        // 5 defenders on the arc / edge of box.
        { role: 'CB', x: -4, z: origin.z - 7 },
        { role: 'CB', x: 0, z: origin.z - 9 },
        { role: 'CM', x: 4, z: origin.z - 7 },
        { role: 'LB', x: -8, z: origin.z - 11 },
        { role: 'RB', x: 8, z: origin.z - 11 },
        // Outlets for the breakaway.
        { role: 'LW', x: -halfW * 0.4, z: 0 },
        { role: 'RW', x: halfW * 0.4, z: 0 },
        { role: 'ST', x: 0, z: -halfL * 0.3 },
        { role: 'AM', x: 0, z: -halfL * 0.15 },
        { role: 'CM', x: -halfW * 0.2, z: -halfL * 0.1 },
      ];
      return {
        name: 'Defending penalty',
        type,
        origin,
        homeSpots,
        awaySpots: holdingShape('away'),
        arrows: [],
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

const TYPE_SET: ReadonlySet<string> = new Set(SET_PIECE_TYPES);
const DELIVERY_SET: ReadonlySet<string> = new Set(DELIVERY_STYLES);

function isSpot(value: unknown): value is SetPieceSpot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.role !== 'string') return false;
  if (typeof v.x !== 'number' || !Number.isFinite(v.x)) return false;
  if (typeof v.z !== 'number' || !Number.isFinite(v.z)) return false;
  return true;
}

/** Type guard for one set-piece. Rejects anything missing the required
 *  scalars, anything whose `homeSpots` / `awaySpots` / `arrows` aren't
 *  arrays, any unknown `type`, and any present-but-invalid `delivery`.
 *  Used when reading the `setPieces?: TacticsSetPiece[]` slot off a
 *  stored layout so a corrupt entry no-ops instead of crashing the
 *  renderer. */
export function isTacticsSetPiece(value: unknown): value is TacticsSetPiece {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;

  if (typeof v.id !== 'string' || v.id.length === 0) return false;
  if (typeof v.name !== 'string' || v.name.length === 0) return false;
  if (typeof v.type !== 'string' || !TYPE_SET.has(v.type)) return false;
  if (typeof v.createdAt !== 'string' || v.createdAt.length === 0) return false;

  // origin
  if (!v.origin || typeof v.origin !== 'object') return false;
  const origin = v.origin as Record<string, unknown>;
  if (typeof origin.x !== 'number' || !Number.isFinite(origin.x)) return false;
  if (typeof origin.z !== 'number' || !Number.isFinite(origin.z)) return false;

  if (!Array.isArray(v.homeSpots)) return false;
  for (const s of v.homeSpots) if (!isSpot(s)) return false;
  if (!Array.isArray(v.awaySpots)) return false;
  for (const s of v.awaySpots) if (!isSpot(s)) return false;
  if (!Array.isArray(v.arrows)) return false;

  // Optional fields — only validate when present.
  if (v.description !== undefined && typeof v.description !== 'string') {
    return false;
  }
  if (
    v.delivery !== undefined &&
    (typeof v.delivery !== 'string' || !DELIVERY_SET.has(v.delivery))
  ) {
    return false;
  }

  return true;
}
